/**
 * Web implementation of React Native's `Alert` API.
 *
 * The platform `Alert.alert()` renders a browser-native alert/confirm on web,
 * which looks out of place in our app. This module mirrors RN's imperative
 * API while rendering an in-app dialog that matches the OpenAI-inspired
 * design system.
 *
 * Mount `<WebAlertHost />` once near the root of the web layout.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type AlertButtonStyle = 'default' | 'cancel' | 'destructive';

export type AlertButton = {
  text?: string;
  onPress?: (value?: string) => void | Promise<void>;
  style?: AlertButtonStyle;
  isPreferred?: boolean;
};

export type AlertOptions = {
  cancelable?: boolean;
  onDismiss?: () => void;
  // Accepted for API parity; unused on web.
  userInterfaceStyle?: 'unspecified' | 'light' | 'dark';
};

type AlertRequest = {
  id: number;
  title: string;
  message?: string;
  buttons: AlertButton[];
  options?: AlertOptions;
};

type Listener = (request: AlertRequest | null) => void;

const listeners = new Set<Listener>();
let nextId = 0;
const queue: AlertRequest[] = [];
let active: AlertRequest | null = null;

function emit() {
  for (const l of listeners) l(active);
}

function show(request: AlertRequest) {
  if (active) {
    queue.push(request);
    return;
  }
  active = request;
  emit();
}

function dismiss() {
  const dismissed = active;
  active = null;
  dismissed?.options?.onDismiss?.();
  const next = queue.shift();
  if (next) {
    active = next;
  }
  emit();
}

export const Alert = {
  alert(
    title: string,
    message?: string,
    buttons?: AlertButton[],
    options?: AlertOptions,
  ): void {
    const resolved =
      buttons && buttons.length > 0
        ? buttons
        : ([{ text: 'OK', style: 'default' }] as AlertButton[]);
    show({ id: ++nextId, title, message, buttons: resolved, options });
  },
  // RN's `prompt` is iOS-only; provide a stub that falls back to message-only
  // so imports don't break if anyone wires it up later.
  prompt(
    title: string,
    message?: string,
    callbackOrButtons?: ((value: string) => void) | AlertButton[],
  ): void {
    const buttons =
      Array.isArray(callbackOrButtons)
        ? callbackOrButtons
        : ([{ text: 'OK', style: 'default' }] as AlertButton[]);
    show({ id: ++nextId, title, message, buttons });
  },
};

// ─── Promise-based confirm helper ────────────────────────────────────

export type ConfirmOptions = {
  title?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
};

/**
 * Promise-based replacement for `window.confirm()` that renders the styled
 * in-app dialog. Resolves to `true` if the user clicks confirm, `false` if
 * they cancel (button, Escape, or backdrop click).
 */
export function confirm(options: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: boolean) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    Alert.alert(
      options.title ?? '',
      options.message,
      [
        {
          text: options.cancelLabel ?? 'Cancel',
          style: 'cancel',
          onPress: () => finish(false),
        },
        {
          text: options.confirmLabel ?? 'OK',
          style: options.destructive ? 'destructive' : 'default',
          onPress: () => finish(true),
        },
      ],
      { cancelable: true, onDismiss: () => finish(false) },
    );
  });
}

// ─── Host component ──────────────────────────────────────────────────

export function WebAlertHost() {
  const [request, setRequest] = useState<AlertRequest | null>(null);
  const [busyIndex, setBusyIndex] = useState<number | null>(null);
  const [portalTarget, setPortalTarget] = useState<Element | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      setPortalTarget(document.body);
    }
  }, []);

  useEffect(() => {
    const listener: Listener = (next) => {
      setRequest(next);
      setBusyIndex(null);
    };
    listeners.add(listener);
    if (active) listener(active);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  // Focus the preferred / last button on open for keyboard a11y.
  useEffect(() => {
    if (!request) return;
    const t = window.setTimeout(() => {
      const el = dialogRef.current?.querySelector<HTMLButtonElement>(
        'button[data-alert-preferred="true"]',
      );
      el?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [request]);

  // Escape → cancel button (or dismiss if cancelable).
  useEffect(() => {
    if (!request) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const cancelIdx = request.buttons.findIndex(
          (b) => b.style === 'cancel',
        );
        if (cancelIdx >= 0) {
          void handlePress(cancelIdx);
        } else if (request.options?.cancelable) {
          dismiss();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  if (!request) return null;
  if (!portalTarget) return null;

  const { title, message, buttons } = request;
  const stacked = buttons.length > 2;
  const preferredIdx = (() => {
    const explicit = buttons.findIndex((b) => b.isPreferred);
    if (explicit >= 0) return explicit;
    const lastNonCancel = [...buttons]
      .map((b, i) => ({ b, i }))
      .reverse()
      .find(({ b }) => b.style !== 'cancel');
    return lastNonCancel ? lastNonCancel.i : buttons.length - 1;
  })();

  async function handlePress(index: number) {
    if (busyIndex !== null) return;
    const button = request!.buttons[index];
    const onPress = button?.onPress;
    if (!onPress) {
      dismiss();
      return;
    }
    let result: unknown;
    try {
      result = onPress();
    } catch {
      dismiss();
      return;
    }
    if (result && typeof (result as Promise<unknown>).then === 'function') {
      setBusyIndex(index);
      try {
        await result;
      } finally {
        // Active may have changed if a new alert was triggered; only
        // dismiss the one that was active when the press started.
        if (active?.id === request!.id) dismiss();
      }
    } else {
      dismiss();
    }
  }

  function handleBackdropClick() {
    if (busyIndex !== null) return;
    if (request!.options?.cancelable === false) return;
    const cancelIdx = request!.buttons.findIndex((b) => b.style === 'cancel');
    if (cancelIdx >= 0) {
      void handlePress(cancelIdx);
    } else if (request!.options?.cancelable) {
      dismiss();
    }
  }

  return createPortal(
    <>
      <div
        className="tabbio-scrim show"
        onClick={handleBackdropClick}
        style={{ zIndex: 100000 }}
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={title ? 'web-alert-title' : undefined}
        aria-describedby={message ? 'web-alert-message' : undefined}
        ref={dialogRef}
        className="tabbio-dialog show"
        style={{ zIndex: 100001 }}
      >
        {title ? (
          <h2 id="web-alert-title" className="tabbio-dialog-title">
            {title}
          </h2>
        ) : null}
        {message ? (
          <p
            id="web-alert-message"
            className="tabbio-dialog-body"
            style={{ whiteSpace: 'pre-wrap' }}
          >
            {message}
          </p>
        ) : null}
        <div className={stacked ? 'tabbio-dialog-actions stacked' : 'tabbio-dialog-actions'}
          style={stacked ? { flexDirection: 'column', alignItems: 'stretch' } : undefined}
        >
          {buttons.map((button, index) => {
            const label = button.text ?? 'OK';
            const style = button.style ?? 'default';
            const isPreferred = index === preferredIdx;
            const isBusy = busyIndex === index;
            const disabled = busyIndex !== null && !isBusy;
            return (
              <AlertButtonView
                key={`${index}-${label}`}
                label={label}
                style={style}
                isPreferred={isPreferred}
                isBusy={isBusy}
                disabled={disabled}
                fullWidth={stacked}
                onClick={() => {
                  void handlePress(index);
                }}
              />
            );
          })}
        </div>
        <style>{`
          @keyframes webAlertSpin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    </>,
    portalTarget,
  );
}

// ─── Internal button view ────────────────────────────────────────────

function AlertButtonView({
  label,
  isPreferred,
  isBusy,
  disabled,
  fullWidth,
  onClick,
}: {
  label: string;
  style: AlertButtonStyle;
  isPreferred: boolean;
  isBusy: boolean;
  disabled: boolean;
  fullWidth: boolean;
  onClick: () => void;
}) {
  // Per Gemini spec: all dialog buttons are plain text pills. Differentiation
  // is by position (right-most = primary), not color. No filled primary, no
  // destructive red.
  return (
    <button
      type="button"
      data-alert-preferred={isPreferred ? 'true' : undefined}
      onClick={onClick}
      disabled={disabled || isBusy}
      className="tabbio-dialog-btn"
      style={{
        flex: fullWidth ? '0 0 auto' : undefined,
        width: fullWidth ? '100%' : undefined,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
      }}
    >
      {isBusy ? (
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: 14,
            height: 14,
            border: '2px solid currentColor',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'webAlertSpin 0.7s linear infinite',
            opacity: 0.85,
          }}
        />
      ) : null}
      <span>{label}</span>
    </button>
  );
}
